import { useState, useEffect, useCallback } from 'react';
import { 
  getDeferredPrompt, 
  clearDeferredPrompt, 
  subscribeToPrompt, 
  subscribeToAppInstalled,
  isPromptAvailable 
} from '@/lib/pwaInstallPrompt';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

type Platform = 'ios' | 'android' | 'desktop' | 'other';
type InstallOutcome = 'accepted' | 'dismissed' | null;

interface UsePWAInstallReturn {
  isInstallable: boolean;
  isInstalled: boolean;
  isInstalling: boolean;
  platform: Platform;
  installApp: () => Promise<boolean>;
  installOutcome: InstallOutcome;
}

const detectPlatform = (): Platform => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'other';
  }
  
  const userAgent = navigator.userAgent || navigator.vendor || '';
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  if (/Windows|Macintosh|Linux/.test(userAgent) && !/Mobile/.test(userAgent)) {
    return 'desktop';
  }
  
  return 'other';
};

const checkIsStandalone = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Check display-mode media query
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check iOS standalone mode
  if ((navigator as any).standalone === true) {
    return true;
  }
  
  // Check Android TWA
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

export const usePWAInstall = (): UsePWAInstallReturn => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() => getDeferredPrompt());
  const [isInstallable, setIsInstallable] = useState(() => isPromptAvailable());
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installOutcome, setInstallOutcome] = useState<InstallOutcome>(null);
  const [platform, setPlatform] = useState<Platform>('other');

  useEffect(() => {
    // Detect platform on mount
    setPlatform(detectPlatform());
    
    const isCurrentlyStandalone = checkIsStandalone();
    const wasInstalled = localStorage.getItem('appInstalled') === 'true';
    
    // If the app was marked as installed but is no longer in standalone mode,
    // the user likely uninstalled the PWA - reset the installation flags
    if (wasInstalled && !isCurrentlyStandalone) {
      console.log('PWA was uninstalled - resetting installation flags');
      localStorage.removeItem('appInstalled');
      localStorage.removeItem('onboardingComplete');
      localStorage.removeItem('currentOnboardingStep');
      setIsInstalled(false);
    } else {
      setIsInstalled(isCurrentlyStandalone);
    }

    // Check if prompt was already captured by global manager
    const existingPrompt = getDeferredPrompt();
    if (existingPrompt) {
      console.log('usePWAInstall: Found existing deferred prompt from global manager');
      setDeferredPrompt(existingPrompt);
      setIsInstallable(true);
    }

    // Subscribe to future prompt events
    const unsubscribePrompt = subscribeToPrompt((prompt) => {
      console.log('usePWAInstall: Received prompt from global manager');
      setDeferredPrompt(prompt);
      setIsInstallable(prompt !== null);
    });

    // Subscribe to app installed events
    const unsubscribeInstalled = subscribeToAppInstalled(() => {
      console.log('usePWAInstall: App installed event received');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setInstallOutcome('accepted');
    });

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
      } else {
        // App is no longer in standalone mode (uninstalled)
        localStorage.removeItem('appInstalled');
        setIsInstalled(false);
      }
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      unsubscribePrompt();
      unsubscribeInstalled();
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const installApp = useCallback(async (): Promise<boolean> => {
    // Try to get prompt from global manager if local state is null
    const promptToUse = deferredPrompt || getDeferredPrompt();
    
    if (!promptToUse) {
      console.log('usePWAInstall: No install prompt available');
      return false;
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      await promptToUse.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await promptToUse.userChoice;
      
      setInstallOutcome(outcome);
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        localStorage.setItem('appInstalled', 'true');
        localStorage.setItem('pendingOnboardingStep', 'email');
      }

      // Clear the deferredPrompt - it can only be used once
      setDeferredPrompt(null);
      clearDeferredPrompt();
      setIsInstalling(false);

      return outcome === 'accepted';
    } catch (error) {
      console.error('Error installing PWA:', error);
      setIsInstalling(false);
      return false;
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    isInstalling,
    platform,
    installApp,
    installOutcome,
  };
};
